import axios from 'axios';
import {
  AxiosInstanceWithVersioning,
  VersioningStrategy,
  withVersioning,
} from 'axios-api-versioning';
import createAuthRefreshInterceptor from 'axios-auth-refresh';
import authApi from '~/authApi';

import { SearchApiRequestParams } from './searchContentApi.api-model';
import {
  mapSearchContentFromApiToVm,
  mapSearchContentParamsFromVmToApi,
} from './searchContentApi.mappers';
import { SearchContentVmResponse } from './searchContentApi.model';

class SearchApiClient {
  public _apiInstance: AxiosInstanceWithVersioning;

  private BASE_SEARCH_CONTENT_ENDPOINT_URL = '/contents';

  constructor() {
    const baseClient = axios.create({
      baseURL: `${process.env.NEXT_PUBLIC_SEARCH_RESOURCE}/v{apiVersion}`,
    });

    this._apiInstance = withVersioning(baseClient, {
      apiVersion: '1',
      versioningStrategy: VersioningStrategy.UrlPath,
    });

    this._apiInstance.interceptors.request.use((req) => {
      const { jwtAccessToken } = authApi;

      if (jwtAccessToken) {
        req.headers = {
          ...req.headers,
          'X-AUTH-TOKEN': jwtAccessToken.accessToken,
        };
      }

      return req;
    });

    createAuthRefreshInterceptor(baseClient, (failedRequest) =>
      authApi
        .refreshAccessToken()
        .then((newToken) => {
          failedRequest.response.config.headers['X-AUTH-TOKEN'] =
            newToken?.accessToken;
          if (newToken) {
            return Promise.resolve();
          } else {
            return Promise.reject();
          }
        })
        .catch(() => {
          return Promise.reject();
        }),
    );
  }

  public async getContents(
    params: SearchApiRequestParams,
  ): Promise<SearchContentVmResponse> {
    const mappedSearchApiParams = mapSearchContentParamsFromVmToApi(params);

    const { data } = await this._apiInstance.post(
      this.BASE_SEARCH_CONTENT_ENDPOINT_URL,
      {
        ...mappedSearchApiParams,
      },
    );

    return mapSearchContentFromApiToVm(data);
  }

  public async getCreatorsFromRFP(
    text: string,
    roster: 'manager' | 'agency' = 'manager',
  ) {
    return this._apiInstance
      .post(`/ai/rfp/creators`, {
        text,
        manager_id: roster === 'manager' ? 'self' : undefined, // send self if you want creators from manager's roster
        size: 10,
      })
      .then((res) => ({
        ...res.data,
        requestId: res.headers['x-request-id'],
      }));
  }

  public async getContentAutocompleteSuggestions(
    query: string,
    size: number = 5,
  ) {
    return this._apiInstance
      .get(`/autocomplete/content?text=${query}&size=${size}`)
      .then((res) => res.data);
  }
}

/**
 * @deprecated Use src/api/search.ts instead
 */
export const searchApi = new SearchApiClient();
